// Helper to fetch all balances
  // (must be defined after fetchCsprBalance, fetchSCspr, fetchWithdrawable)
  // Moved to after all fetch* functions and before first usage
import React, { useState, useEffect, useCallback } from "react";
import { intervalToDuration } from 'date-fns';
import cn from "classnames";
import styles from "./LiquidStaking.module.sass";
import useCasperWallet from "../../customHooks/useCasperWallet";
import { useSelector } from "react-redux";
import { DEFAULT_CASPER_GAS_PRICE } from "../../utils/config";
import { csprToMotes } from "../../utils/casper-utils";
import Modal from "../../components/Modal";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";

const LiquidStaking = () => {
  const click = window.csprclick;
  const [tab, setTab] = useState("stake");
  const [amount, setAmount] = useState("");
  const [csprBalance, setCsprBalance] = useState(0);
  const [sCsprBalance, setSCsprBalance] = useState(0);
  // Removed loadingScspr state
  // eslint-disable-next-line no-unused-vars
  const [scsprError, setScsprError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [failedData, setFailedData] = useState(null);
  const apr = 7.2;
  const [withdrawable, setWithdrawable] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [pending, setPending] = useState(0);
  const [pendingList, setPendingList] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  
  // Live timer for pending modal
  useEffect(() => {
    let timer;
    if (showPendingModal) {
      timer = setInterval(() => {
        setNowTs(Date.now());
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [showPendingModal]);

  // Casper
  const { getCsprScprRate, stake, unstake, getScsprBalance, getAddressClaims, withdrawCspr, getBalance } = useCasperWallet(click);
  // Withdraw state
  const [withdrawing, setWithdrawing] = useState(false);
  const [showWithdrawConfirmModal, setShowWithdrawConfirmModal] = useState(false);
  const [showWithdrawSuccessModal, setShowWithdrawSuccessModal] = useState(false);
  const [withdrawTxHash, setWithdrawTxHash] = useState(null);
  const [withdrawError, setWithdrawError] = useState(null);
  // Withdraw handler
  const handleWithdraw = () => {
    setWithdrawError(null);
    setShowWithdrawConfirmModal(true);
  };

  const handleConfirmWithdraw = async () => {
    setShowWithdrawConfirmModal(false);
    setWithdrawing(true);
    setWithdrawError(null);
    setWithdrawTxHash(null);
    
    try {
      const txHash = await withdrawCspr({
        networkName: casperChainConfig?.defaultNetworkName,
        contractPackageHash,
        entryPoint: 'claim',
        paymentAmount: DEFAULT_CASPER_GAS_PRICE,
        publicKey: publicKey,
        onStatusUpdate: (status, data) => {
          console.log('Withdraw status update:', status, data);
          if (status === 'completed' && data.transactionHash) {
            setWithdrawTxHash(data.transactionHash);
            setShowWithdrawSuccessModal(true);
            // Refresh balances after withdraw
            fetchAllBalances();
          } else if (status === 'failed') {
            setWithdrawError(data.message || 'Withdraw transaction failed');
          }
        },
      });
      
      if (!txHash) {
        setWithdrawError('No transaction hash returned. Please try again.');
      }
    } catch (err) {
      setWithdrawError('Withdraw failed: ' + (err?.message || err));
    } finally {
      setWithdrawing(false);
    }
  };
  const [exchangeRate, setExchangeRate] = useState(1.102);
  const [loadingRate, setLoadingRate] = useState(false);
  const [rateError, setRateError] = useState(null);
  // Staking state
  const [staking, setStaking] = useState(false);
  const [stakeError, setStakeError] = useState(null);
  const [stakeTxHash, setStakeTxHash] = useState(null);
  // Unstaking state
  const [unstaking, setUnstaking] = useState(false);
  const [unstakeError, setUnstakeError] = useState(null);
  const [unstakeTxHash, setUnstakeTxHash] = useState(null);
  const [showUnstakeConfirmModal, setShowUnstakeConfirmModal] = useState(false);
  const [showUnstakeSuccessModal, setShowUnstakeSuccessModal] = useState(false);
  const [showUnstakeFailedModal, setShowUnstakeFailedModal] = useState(false);
  const [unstakeSuccessData, setUnstakeSuccessData] = useState(null);
  const [unstakeFailedData, setUnstakeFailedData] = useState(null);
  // Handle Unstake
  const handleUnstake = async () => {
    setUnstakeError(null);
    setUnstakeTxHash(null);
    if (!nodeUrl || !contractPackageHash || !publicKey) {
      setUnstakeError("Missing network or wallet info");
      return;
    }
    setShowUnstakeConfirmModal(true);
  };

  const handleConfirmUnstake = async () => {
    setShowUnstakeConfirmModal(false);
    setUnstaking(true);
    setUnstakeError(null);
    setUnstakeTxHash(null);
    
    try {
      const txHash = await unstake({
        networkName: casperChainConfig?.defaultNetworkName,
        contractPackageHash,
        entryPoint: "unstake",
        amount: String(csprToMotes(amount)),
        paymentAmount: DEFAULT_CASPER_GAS_PRICE,
        publicKey: publicKey,
        onStatusUpdate: (status, data) => {
          console.log('Unstake status update:', status, data);
          if (status === 'completed' && data.transactionHash) {
            setUnstakeTxHash(data.transactionHash);
            setUnstakeSuccessData({
              unstaked: Number(amount).toFixed(2),
              received: (amount * exchangeRate).toFixed(2),
              exchangeRate,
            });
            setShowUnstakeSuccessModal(true);
            // Clear amount after successful unstake to show placeholder
            setAmount(null);
            // Refresh withdrawable and sCSPR balance after successful unstake
            fetchAllBalances();
          } else if (status === 'failed') {
            setUnstakeError(data.message || "Unstake transaction failed");
            setUnstakeFailedData({
              error: data.message || "Unstake transaction failed",
              unstaked: Number(amount).toFixed(2),
              exchangeRate,
            });
            setShowUnstakeFailedModal(true);
          }
        },
      });
      
      if (!txHash) {
        setUnstakeError("No transaction hash returned. Please try again.");
        setUnstakeFailedData({
          error: "No transaction hash returned. Please try again.",
          unstaked: Number(amount).toFixed(2),
          exchangeRate,
        });
        setShowUnstakeFailedModal(true);
      }
    } catch (err) {
      setUnstakeError("Unstake failed: " + (err?.message || err));
      setUnstakeFailedData({
        error: err?.message || String(err),
        unstaked: Number(amount).toFixed(2),
        exchangeRate,
      });
      setShowUnstakeFailedModal(true);
    } finally {
      setUnstaking(false);
    }
  };

  // Get Casper chain config from Redux
  const casperChainConfig = useSelector(state => state.casper.casperChainConfig);
  const nodeUrl = casperChainConfig?.rpcUrl;
  const contractPackageHash = casperChainConfig?.contractPackageHash;
  const contractHash = casperChainConfig?.contractHash;

  const casperActiveAccount = useSelector(state => state.casper.casperActiveAccount);

  // Get Casper public key from Redux
  const publicKey = casperActiveAccount?.public_key


  // Fetch and update CSPR balance using getBalance
  const fetchCsprBalance = useCallback(async (publicKey, nodeUrl) => {
    if (!publicKey || !nodeUrl) {
      setCsprBalance(0);
      return;
    }
    try {
      const motes = await getBalance(publicKey, nodeUrl);
      setCsprBalance(Number(motes) / 1e9);
    } catch (err) {
      setCsprBalance(0);
    }
  }, [getBalance]);

  useEffect(() => {
    fetchCsprBalance(publicKey, nodeUrl);
  }, [fetchCsprBalance, publicKey, nodeUrl]);

  // Fetch cspr withdrawable balance
  const fetchWithdrawable = useCallback(async ({ nodeUrl, contractHash, publicKey }) => {
    if (!nodeUrl || !contractHash || !publicKey) {
      setWithdrawable(0);
      setPending(0);
      setPendingList([]);
      return;
    }
    try {
      const claims = await getAddressClaims(nodeUrl, contractHash, publicKey);
      // claims.claimable and claims.pending are in motes, convert to CSPR
      const claimableCSPR = claims && claims.claimable ? claims.claimable / 1e9 : 0;
      const pendingCSPR = claims && claims.pending ? claims.pending / 1e9 : 0;
      setWithdrawable(claimableCSPR);
      setPending(pendingCSPR);
      setPendingList(Array.isArray(claims.pendingList) ? claims.pendingList : []);
    } catch (err) {
      console.error('Failed to fetch withdrawable balance:', err);
      setWithdrawable(0);
      setPending(0);
      setPendingList([]);
    }
  }, [getAddressClaims]);
  
  useEffect(() => {
    fetchWithdrawable({ nodeUrl, contractHash, publicKey });
  }, [fetchWithdrawable, nodeUrl, contractHash, publicKey]);

  // Fetch sCSPR balance
  const fetchSCspr = useCallback(async ({ nodeUrl, contractHash, publicKey }) => {
    if (!nodeUrl || !contractHash || !publicKey) {
      setSCsprBalance(0);
      return;
    }
    setScsprError(null);
    try {
      const balance = await getScsprBalance(nodeUrl, contractHash, publicKey);
      setSCsprBalance(balance / 1e9); // Convert motes to sCSPR
    } catch (err) {
      setScsprError('Failed to fetch sCSPR balance');
      setSCsprBalance(0);
    }
  }, [getScsprBalance]);
  
  useEffect(() => {
    fetchSCspr({ nodeUrl, contractHash, publicKey });
  }, [fetchSCspr, nodeUrl, contractHash, publicKey]);

  // Fetch exchange rate
  useEffect(() => {
    if (!contractHash) return;
    const fetchRate = async () => {
      setLoadingRate(true);
      setRateError(null);
      try {
        const result = await getCsprScprRate(nodeUrl, contractHash);
        setExchangeRate(result.rate);
      } catch (err) {
        setRateError("Failed to fetch exchange rate");
      } finally {
        setLoadingRate(false);
      }
    };
    fetchRate();
  }, [getCsprScprRate, nodeUrl, contractHash]);

  const fetchAllBalances = useCallback(
    async () => {
      if (nodeUrl && contractHash && publicKey) {
        await fetchSCspr({ nodeUrl, contractHash, publicKey });
        await fetchWithdrawable({ nodeUrl, contractHash, publicKey });
      }
    },
    [fetchSCspr, fetchWithdrawable, nodeUrl, contractHash, publicKey],
  );

  // Calculate sCSPR to receive
  const scsprToReceive = amount && exchangeRate ? (Number(amount) / exchangeRate).toFixed(2) : "0.00";


  // Minimum staking amount
  const MIN_STAKE = 500;

  // Handle Stake
  const handleStake = async () => {
    setStakeError(null);
    setStakeTxHash(null);
    if (!nodeUrl || !contractPackageHash || !publicKey) {
      setStakeError("Missing network or wallet info");
      return;
    }
    if (Number(amount) < MIN_STAKE) {
      setStakeError(`Minimum stake is ${MIN_STAKE} CSPR`);
      return;
    }
    // Instead of staking immediately, show confirm modal
    setShowConfirmModal(true);
  };

  const handleConfirmStake = async () => {
    setShowConfirmModal(false);
    setStaking(true);
    setStakeError(null);
    setStakeTxHash(null);
    
    try {
      const txHash = await stake({
        nodeUrl,
        networkName: casperChainConfig?.defaultNetworkName,
        contractPackageHash,
        entryPoint: "stake",
        amount: String(csprToMotes(amount)),
        paymentAmount: DEFAULT_CASPER_GAS_PRICE,
        publicKey: publicKey,
        onStatusUpdate: (status, data) => {
          console.log('Stake status update:', status, data);
          // You can add UI updates here based on status
          // For example, update loading states, show progress, etc.
          if (status === 'completed' && data.transactionHash) {
            setStakeTxHash(data.transactionHash);
            setSuccessData({
              staked: Number(amount).toFixed(2),
              received: scsprToReceive,
              exchangeRate,
            });
            setShowSuccessModal(true);
            // Clear amount after successful stake to show placeholder
            setAmount(null);
            // Refresh balances after successful stake
            fetchAllBalances();
          } else if (status === 'failed') {
            setStakeError(data.message || "Transaction failed");
            setFailedData({
              error: data.message || "Transaction failed",
              staked: Number(amount).toFixed(2),
              exchangeRate,
            });
            setShowFailedModal(true);
          }
        },
      });
      
      if (!txHash) {
        setStakeError("No transaction hash returned. Please try again.");
        setFailedData({
          error: "No transaction hash returned. Please try again.",
          staked: Number(amount).toFixed(2),
          exchangeRate,
        });
        setShowFailedModal(true);
      }
    } catch (err) {
      console.error("Stake error:", err);
      setStakeError("Stake failed: " + (err?.message || err));
      setFailedData({
        error: err?.message || String(err),
        staked: Number(amount).toFixed(2),
        exchangeRate,
      });
      setShowFailedModal(true);
    } finally {
      setStaking(false);
    }
  };

  return (
    <div className={styles.liquidStakingRoot}>
      <div className={styles.liquidStakingContainerBox}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <img
              src="/images/casper.png"
              alt="casper logo"
              className={styles.headerLogo}
            />
            <span className={styles.headerTitle}>Liquid Staking</span>
          </div>
          <div className={styles.headerRight}>
            <span>APR {apr}%</span>
            <span>
              <i className={cn("icon-info", styles.infoIcon)} />
            </span>
          </div>
        </div>

          {/* Body */}
          <div className={styles.body}>
            {/* Check if Casper wallet is connected */}
            {!casperActiveAccount ? (
              <div className={styles.errorText}>
                Please connect your Casper wallet to use Liquid Staking.
              </div>
            ) : !casperChainConfig ? (
              <div className={styles.errorText}>
                Loading Casper configuration...
              </div>
            ) : (
              <>
                {/* Exchange Rate */}
                <div className={styles.exchangeRateBox}>
                  <div className={styles.exchangeRateLabel}>Exchange Rate</div>
                  <div className={styles.exchangeRateValue}>
                    {loadingRate ? (
                      <b>Loading...</b>
                    ) : rateError ? (
                      <b className={styles.errorText}>{rateError}</b>
                    ) : (
                      <b>1 sCSPR = {exchangeRate} CSPR</b>
                    )}
                  </div>
                </div>

                {/* Balances */}
                <div className={styles.balanceGrid}>
                  <div className={styles.balanceBox}>
                    <div className={styles.balanceLabel}>CSPR Balance</div>
                    <div className={styles.balanceValue}>
                      {csprBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CSPR
                    </div>
                  </div>
                  <div className={styles.balanceBox}>
                    <div className={styles.balanceLabel}>sCSPR Balance</div>
                    <div className={styles.balanceValue}>
                      {sCsprBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} sCSPR
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className={styles.tabs}>
                  <div className={styles.tabsList}>
                    <button
                      className={cn(styles.tabsTrigger, tab === "stake" && styles.active)}
                      type="button"
                      onClick={() => setTab("stake")}
                      aria-selected={tab === "stake"}
                      tabIndex={0}
                      role="tab"
                    >
                      Stake
                    </button>
                    <button
                      className={cn(styles.tabsTrigger, tab === "unstake" && styles.active)}
                      type="button"
                      onClick={() => setTab("unstake")}
                      aria-selected={tab === "unstake"}
                      tabIndex={0}
                      role="tab"
                    >
                      Unstake
                    </button>
                  </div>
                </div>

                {/* Input Row */}
                <div className={styles.inputBox}>
                  <div className={styles.inputLabelRow}>
                    <span>{tab === "stake" ? "CSPR Amount" : "sCSPR Amount"}</span>
                    <span>
                      Available: {(tab === "stake" ? csprBalance : sCsprBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {tab === "stake" ? "CSPR" : "sCSPR"} <span className={styles.maxButton} onClick={() => setAmount(String(tab === "stake" ? csprBalance : sCsprBalance))}>MAX</span>
                    </span>
                  </div>
                  <div className={styles.inputRow}>
                    <input
                      className={styles.input}
                      type="number"
                      min={0}
                      placeholder={tab === "stake" ? "Enter CSPR amount to stake" : "Enter sCSPR amount to unstake"}
                      value={amount}
                      onChange={e => {
                        let val = e.target.value;
                        if (val === "") {
                          setAmount("");
                          return;
                        }
                        val = Number(val);
                        if (isNaN(val)) {
                          setAmount("");
                          return;
                        }
                        if (tab === "stake") {
                          if (val > csprBalance) {
                            setAmount(String(csprBalance));
                          } else {
                            setAmount(String(val));
                          }
                        } else {
                          if (val > sCsprBalance) {
                            setAmount(String(sCsprBalance));
                          } else {
                            setAmount(String(val));
                          }
                        }
                      }}
                    />
                  </div>
                  <div className={styles.estimatedRow}>
                    → ≈ {(tab === "stake"
                      ? (amount ? (Number(amount) / exchangeRate).toFixed(2) : "0.00") + " sCSPR"
                      : (amount ? (Number(amount) * exchangeRate).toFixed(2) : "0.00") + " CSPR")}
                  </div>
                </div>

                {/* Stake/Unstake Button */}
                {tab === "stake" ? (
                  <>
                    <button
                      className={styles.stakeButton}
                      style={{ marginBottom: 16, marginTop: 0 }}
                      onClick={handleStake}
                      disabled={
                        staking ||
                        !amount ||
                        Number(amount) < MIN_STAKE ||
                        Number(amount) > csprBalance
                      }
                    >
                      {staking
                        ? "Staking..."
                        : amount && Number(amount) > 0 && Number(amount) < MIN_STAKE
                          ? `Minimum stake is ${MIN_STAKE} CSPR`
                          : `Stake CSPR`}
                    </button>
                  </>
                ) : (
                  <button
                    className={styles.stakeButton}
                    style={{ marginBottom: 16, marginTop: 0 }}
                    onClick={handleUnstake}
                    disabled={
                      unstaking ||
                      !amount ||
                      Number(amount) <= 0 ||
                      Number(amount) > sCsprBalance
                    }
                  >
                    {unstaking ? "Unstaking..." : "Unstake sCSPR"}
                  </button>
                )}

                {/* Withdrawable & Pending Link */}
                <div className={styles.withdrawBox}>
                  <div className={styles.withdrawCol}>
                    <div className={styles.withdrawLabelRow}>
                      <div className={styles.withdrawLabel}>
                        Withdrawable
                        {pendingList && pendingList.length > 0 && (
                          <span
                            className={styles.viewPendingLink}
                            onClick={() => setShowPendingModal(true)}
                            tabIndex={0}
                            role="button"
                            aria-label="View Pending Unbonding"
                          >
                            (View Pending)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.withdrawValue}>{withdrawable.toFixed(2)} CSPR</div>
                  </div>
                  <button
                    className={styles.withdrawButton}
                    onClick={handleWithdraw}
                    disabled={withdrawing || withdrawable <= 0}
                  >
                    {withdrawing ? 'Withdrawing...' : 'Withdraw'}
                  </button>
                </div>

                {/* Info Box */}
                <div className={styles.infoBox}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoIcon}>
                      <i className={cn("icon-info")} />
                    </span>
                    <span className={styles.infoText}>
                      Stake CSPR to receive sCSPR tokens that accrue staking rewards.<br />
                      <span className={styles.infoHighlight}>7-eras unbonding period applies when unstaking sCSPR tokens.</span>
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Confirm Stake Modal */}
          <Modal visible={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
            <div className={styles.confirmModalTitle}>Confirm Stake</div>
            <div className={styles.confirmModalBox}>
              <div className={styles.confirmModalSection}>
                <div className={styles.confirmModalLabel}>You will stake</div>
                <div className={styles.confirmModalValue}>{Number(amount).toFixed(2)} CSPR</div>
              </div>
              <div className={styles.confirmModalSection}>
                <div className={styles.confirmModalLabel}>You will receive</div>
                <div className={styles.confirmModalValue}>
                  ≈ {scsprToReceive} sCSPR
                </div>
              </div>
              <div className={styles.confirmModalSection}>
                <div className={styles.confirmModalLabel}>Exchange Rate</div>
                <div className={styles.confirmModalValue}>
                  1 sCSPR = {exchangeRate} CSPR
                </div>
              </div>
              <div className={styles.confirmModalActions}>
                <button
                  className={styles.confirmModalCancel}
                  onClick={() => setShowConfirmModal(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={styles.confirmModalConfirm}
                  onClick={handleConfirmStake}
                  type="button"
                >
                  Confirm
                </button>
              </div>
            </div>
          </Modal>

          {/* Success Modal */}
          {showSuccessModal && (
            <Modal visible={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
              <div className={styles.successModalTitle}>Transaction Complete</div>
              <div className={styles.successModalBox}>
                <div className={styles.successModalCheck}>
                  <IoMdCheckmarkCircleOutline/>
                </div>
                <div className={styles.successModalSubtitle}>Staking Successful!</div>
                <div className={styles.successModalDesc}>
                  Your CSPR has been staked and you've received sCSPR tokens.
                </div>
                <div className={styles.successModalInfo}>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Staked:</span>
                    <span className={styles.successModalInfoValue}>{successData?.staked} CSPR</span>
                  </div>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Received:</span>
                    <span className={styles.successModalInfoValue}>{successData?.received} sCSPR</span>
                  </div>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Exchange Rate:</span>
                    <span className={styles.successModalInfoValue}>1 sCSPR = {successData?.exchangeRate} CSPR</span>
                  </div>
                  {stakeTxHash && (
                    <div className={styles.successModalInfoRow}>
                      <span className={styles.successModalInfoLabel}>Tx Hash:</span>
                    <span className={styles.successModalInfoValue}>{stakeTxHash}</span>
                    </div>
                  )}
                </div>
                <button
                  className={styles.successModalButton}
                  onClick={() => setShowSuccessModal(false)}
                >
                  Close
                </button>
              </div>
            </Modal>
          )}

          {/* Failed Modal */}
          {showFailedModal && (
            <Modal visible={showFailedModal} onClose={() => setShowFailedModal(false)}>
              <div className={styles.failedModalTitle}>Transaction Failed</div>
              <div className={styles.successModalBox}>
                <div className={styles.failedModalCheck}>
                  <i className="icon-close" />
                </div>
                <div className={styles.failedModalSubtitle}>Staking Failed</div>
                <div className={styles.successModalDesc}>
                  {stakeError || failedData?.error || 'An error occurred during staking.'}
                </div>
                <div className={styles.successModalInfo}>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Staked:</span>
                    <span className={styles.successModalInfoValue}>{failedData?.staked} CSPR</span>
                  </div>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Exchange Rate:</span>
                    <span className={styles.successModalInfoValue}>1 sCSPR = {failedData?.exchangeRate} CSPR</span>
                  </div>
                </div>
                <button
                  className={styles.failedModalButton}
                  onClick={() => setShowFailedModal(false)}
                >
                  Close
                </button>
              </div>
            </Modal>
          )}

          {/* Withdraw Confirm Modal */}
          <Modal visible={showWithdrawConfirmModal} onClose={() => setShowWithdrawConfirmModal(false)}>
            <div className={styles.confirmModalTitle}>Confirm Withdraw</div>
            <div className={styles.confirmModalBox}>
              <div className={styles.confirmModalSection}>
                <div className={styles.confirmModalLabel}>You will withdraw</div>
                <div className={styles.confirmModalValue}>{withdrawable.toFixed(2)} CSPR</div>
              </div>
              <div className={styles.confirmModalActions}>
                <button
                  className={styles.confirmModalCancel}
                  onClick={() => setShowWithdrawConfirmModal(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={styles.confirmModalConfirm}
                  onClick={handleConfirmWithdraw}
                  type="button"
                  disabled={withdrawing}
                >
                  {withdrawing ? 'Withdrawing...' : 'Confirm'}
                </button>
              </div>
              {withdrawError && <div className={styles.errorText}>{withdrawError}</div>}
            </div>
          </Modal>

          {/* Withdraw Success Modal */}
          <Modal visible={showWithdrawSuccessModal} onClose={() => setShowWithdrawSuccessModal(false)}>
            <div className={styles.successModalTitle}>Withdraw Complete</div>
            <div className={styles.successModalBox}>
              <div className={styles.successModalCheck}>
                <IoMdCheckmarkCircleOutline/>
              </div>
              <div className={styles.successModalSubtitle}>Withdrawal Successful!</div>
              <div className={styles.successModalDesc}>
                Your CSPR has been withdrawn to your wallet.
              </div>
              <div className={styles.successModalInfo}>
                <div className={styles.successModalInfoRow}>
                  <span className={styles.successModalInfoLabel}>Withdrawn:</span>
                  <span className={styles.successModalInfoValue}>{withdrawable.toFixed(2)} CSPR</span>
                </div>
                {withdrawTxHash && (
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Tx Hash:</span>
                    <span className={styles.successModalInfoValue}>{withdrawTxHash}</span>
                  </div>
                )}
              </div>
              <button
                className={styles.successModalButton}
                onClick={async () => {
                  setShowWithdrawSuccessModal(false);
                  setWithdrawTxHash(null);
                  // Refresh balances after withdraw
                  await fetchAllBalances();
                }}
              >
                Close
              </button>
            </div>
          </Modal>

          {/* Pending Modal */}
          <Modal visible={showPendingModal} onClose={() => setShowPendingModal(false)}>
            <div className={styles.unbondingModalBox}>
              <div className={styles.unbondingModalTitle}>Unstaking Status</div>
              <div className={styles.unbondingModalDesc}>
                These are your pending unbonding requests. CSPR will be available after the timer reaches zero.
              </div>
              {/* Total Unstaking Amount */}
              {pendingList.length > 0 && (
                <div className={styles.totalUnstakingBox}>
                  <span className={styles.totalUnstakingLabel}>Total Unstaking:</span>
                  <span className={styles.totalUnstakingValue}>
                    {Number(pendingList.reduce((sum, item) => sum + (item.amount || 0), 0) / 1e9).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} CSPR
                  </span>
                </div>
              )}
              <div
                className={styles.unbondingModalList}
              >
                <div className={styles.unbondingModalFade} />
                {pendingList.length === 0 ? (
                  <div className={styles.unbondingModalEmpty}>No pending unbonding requests.</div>
                ) : (
                  pendingList.map((item, idx) => {
                    const claimDate = item.claimTime * 1000;
                    const secondsLeft = Math.max(0, Math.floor((claimDate - nowTs) / 1000));
                    let timeString = 'Available soon';
                    if (secondsLeft > 0) {
                      const duration = intervalToDuration({ start: nowTs, end: claimDate });
                      const { hours = 0, minutes = 0, seconds = 0 } = duration;
                      timeString = `${hours}h ${minutes}m ${seconds}s`;
                    }
                    return (
                      <div key={item.unstakeId} className={styles.unbondingModalItem}>
                        <div>
                          <div className={styles.unbondingModalAmount}>{(item.amount / 1e9).toFixed(2)} CSPR</div>
                          <div className={styles.unbondingModalId}>Unstake ID: {item.unstakeId}</div>
                        </div>
                        <div className={styles.unbondingModalTimer}>
                          <span>{timeString}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <button
                className={styles.unbondingModalClose}
                onClick={() => setShowPendingModal(false)}
              >
                Close
              </button>
            </div>
          </Modal>

          {/* Unstake Confirm Modal */}
          <Modal visible={showUnstakeConfirmModal} onClose={() => setShowUnstakeConfirmModal(false)}>
            <div className={styles.confirmModalTitle}>Confirm Unstake</div>
            <div className={styles.confirmModalBox}>
              <div className={styles.confirmModalSection}>
                <div className={styles.confirmModalLabel}>You will unstake</div>
                <div className={styles.confirmModalValue}>{Number(amount).toFixed(2)} sCSPR</div>
              </div>
              <div className={styles.confirmModalSection}>
                <div className={styles.confirmModalLabel}>You will receive</div>
                <div className={styles.confirmModalValue}>
                  ≈ {(amount * exchangeRate).toFixed(2)} CSPR
                </div>
              </div>
              <div className={styles.confirmModalSection}>
                <div className={styles.confirmModalLabel}>Exchange Rate</div>
                <div className={styles.confirmModalValue}>
                  1 sCSPR = {exchangeRate} CSPR
                </div>
              </div>
              <div className={styles.confirmModalActions}>
                <button
                  className={styles.confirmModalCancel}
                  onClick={() => setShowUnstakeConfirmModal(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={styles.confirmModalConfirm}
                  onClick={handleConfirmUnstake}
                  type="button"
                >
                  Confirm
                </button>
              </div>
            </div>
          </Modal>

          {/* Unstake Success Modal */}
          {showUnstakeSuccessModal && (
            <Modal visible={showUnstakeSuccessModal} onClose={() => setShowUnstakeSuccessModal(false)}>
              <div className={styles.successModalTitle}>Transaction Submitted</div>
              <div className={styles.successModalBox}>
                <div className={styles.unstakeSuccessIconBox}>
                  <span className={styles.unstakeSuccessIcon}><svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#ff4d4f" strokeWidth="2"/><path d="M12 8v4l2 2" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                </div>
                <div className={styles.successModalSubtitle}>Unstaking Initiated</div>
                <div className={styles.successModalDesc}>
                  Your unstaking request has been submitted. CSPR will be available for withdraw after the <b>7-eras unbonding period</b>.
                </div>
                <div className={styles.successModalInfo}>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Unstaked:</span>
                    <span className={styles.successModalInfoValue}>{unstakeSuccessData?.unstaked} sCSPR</span>
                  </div>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Will receive:</span>
                    <span className={styles.successModalInfoValue}>{unstakeSuccessData?.received} CSPR</span>
                  </div>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Exchange Rate:</span>
                    <span className={styles.successModalInfoValue}>1 sCSPR = {unstakeSuccessData?.exchangeRate} CSPR</span>
                  </div>
                  {unstakeTxHash && (
                    <div className={styles.successModalInfoRow}>
                      <span className={styles.successModalInfoLabel}>Tx Hash:</span>
                    <span className={styles.successModalInfoValue}>{unstakeTxHash}</span>
                    </div>
                  )}
                </div>
                <button
                  className={styles.successModalButton}
                  onClick={() => {
                    setShowUnstakeSuccessModal(false);
                    setAmount(0);
                  }}
                >
                  Close
                </button>
              </div>
            </Modal>
          )}

          {/* Unstake Failed Modal */}
          {showUnstakeFailedModal && (
            <Modal visible={showUnstakeFailedModal} onClose={() => setShowUnstakeFailedModal(false)}>
              <div className={styles.failedModalTitle}>Transaction Failed</div>
              <div className={styles.successModalBox}>
                <div className={styles.failedModalCheck}>
                  <i className="icon-close" />
                </div>
                <div className={styles.failedModalSubtitle}>Unstaking Failed</div>
                <div className={styles.successModalDesc}>
                  {unstakeError || unstakeFailedData?.error || 'An error occurred during unstaking.'}
                </div>
                <div className={styles.successModalInfo}>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Unstaked:</span>
                    <span className={styles.successModalInfoValue}>{unstakeFailedData?.unstaked} sCSPR</span>
                  </div>
                  <div className={styles.successModalInfoRow}>
                    <span className={styles.successModalInfoLabel}>Exchange Rate:</span>
                    <span className={styles.successModalInfoValue}>1 sCSPR = {unstakeFailedData?.exchangeRate} CSPR</span>
                  </div>
                </div>
                <button
                  className={styles.failedModalButton}
                  onClick={() => setShowUnstakeFailedModal(false)}
                >
                  Close
                </button>
              </div>
            </Modal>
          )}

        {/* Branding Footer */}
        <div className={styles.brandingFooter}>
          <div className={styles.brandingRow}>
            Powered by <img src="/images/casper-text.png" alt="casper logo" className={styles.brandingLogo} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiquidStaking;
